import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { SchoolInfoService } from '../header/school-info.service';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { BrandService } from '../brand/brand.service';
import { HeaderService } from '../header/header.service';
import { commonFunctionService } from '../utils/commonFunctions';

declare var customToastr: any;
declare var $: any;

// Visible replacement for HeaderV2Component's bar. HeaderV2Component stays
// mounted (visually hidden via CSS — see layout.component.html/css) purely
// to keep its class-config modal (#class-config-popup) and TC-generation
// modal (#modalTc) alive and working exactly as before; this component
// never duplicates their markup/logic. The progress ring here
// (data-toggle="modal" data-target="#class-config-popup") and the New
// Admission button (data-target="#newadmission-v2") trigger those same
// existing modals via plain Bootstrap data-attributes (DOM id-based, not
// Angular component state), so they work with zero wiring back to
// HeaderV2Component.
//
// School name/image/state/locality are read from the schoolAllInfo
// localStorage cache that HeaderV2Component's own ngOnInit already writes
// on every app load (see HeaderV2Component.ts's schoolInfo() subscribe) —
// the same cache groups-v2/expense-report/attendance-report/etc. already
// read elsewhere in this app, rather than this component re-fetching the
// same data with its own API call.
//
// The progress ring's percentage, however, is NOT cached anywhere — only
// written directly into HeaderV2Component's own #percent-progress/
// #percent-text DOM nodes via jQuery (see its getClassConfigStatus()).
// Reusing that id here would collide with the hidden instance, so this
// component calls the same stateless HeaderService.getClassConfigStatus()
// endpoint itself and renders the percentage through normal Angular
// binding instead of direct DOM manipulation.
@Component({
    selector: 'app-header-v3',
    templateUrl: './header-v3.component.html',
    styleUrls: ['./header-v3.component.css'],
    providers: [HeaderService],
})
export class HeaderV3Component implements OnInit, OnDestroy {
    private schoolInfoSub?: Subscription;
    brand = this.brandService.brand;

    loginMode = '';
    isFranchise = 0;
    staffName = '';
    permissions: any = {};

    schoolName = '';
    schoolImage = '';
    schoolState = '';
    schoolLocality = '';

    classConfigPercentage = 0;

    currentDate = new Date();

    constructor(
        private brandService: BrandService,
        private headerService: HeaderService,
        private router: Router,
        private translate: TranslateService,
        private commonFunction: commonFunctionService,
        private schoolInfoService: SchoolInfoService,
    ) {}

    ngOnInit(): void {
        this.resolveLoginMode();
        this.resolveIsFranchise();
        this.resolvePermissions();
        this.staffName = localStorage.getItem('staffName') || '';
        this.schoolInfoSub = this.schoolInfoService.schoolInfo$.subscribe((info) => {
            if (info) {
                this.schoolName = info.schoolName;
                this.schoolImage = info.schoolImage;
                this.schoolState = info.state;
                this.schoolLocality = info.locality;
            }
        });
        if (this.loginMode === 'WEB_ADMIN' && this.isFranchise === 0) {
            this.loadClassConfigPercentage();
        }
    }

    // Same localStorage/cookie race as loginMode/isFranchise above —
    // adminPermissionlist is only ever written to a cookie right after
    // login.
    private resolvePermissions(): void {
        let adminPermissionlist = localStorage.getItem('adminPermissionlist');
        if (!adminPermissionlist) {
            const cookiePermissionlist = this.getCookieValue('adminPermissionlist');
            if (cookiePermissionlist) {
                adminPermissionlist = cookiePermissionlist;
                localStorage.setItem('adminPermissionlist', cookiePermissionlist);
            }
        }
        if (adminPermissionlist) {
            try {
                this.permissions = JSON.parse(adminPermissionlist);
            } catch {
                this.permissions = {};
            }
        }
    }

    // Same cookie/localStorage race HeaderV2Component documents on its own
    // loginMode/isFranchise reads: right after login these are only ever
    // written to a cookie, so a *ngIf gated purely on localStorage (e.g.
    // this header's New Admission button or progress ring) would fail
    // until the next full page refresh without this fallback.
    private resolveLoginMode(): void {
        this.loginMode = localStorage.getItem('loginMode') || '';
        if (!this.loginMode) {
            const cookieLoginMode = this.getCookieValue('loginMode');
            if (cookieLoginMode) {
                this.loginMode = cookieLoginMode;
                localStorage.setItem('loginMode', cookieLoginMode);
            }
        }
    }

    private resolveIsFranchise(): void {
        let isFranchiseValue = localStorage.getItem('isFranchise');
        if (!isFranchiseValue) {
            const cookieIsFranchise = this.getCookieValue('isFranchise');
            if (cookieIsFranchise) {
                isFranchiseValue = cookieIsFranchise;
                localStorage.setItem('isFranchise', cookieIsFranchise);
            }
        }
        this.isFranchise = parseInt(isFranchiseValue, 10) || 0;
    }

    // Right after login the token only exists in a cookie; reading only
    // localStorage made the config-progress call go out with no token.
    private resolveAuthToken(): string | null {
        let token = localStorage.getItem('usertoken');
        if (!token) {
            token = this.getCookieValue('usertoken');
            if (token) {
                localStorage.setItem('usertoken', token);
            }
        }
        return token;
    }

    private getCookieValue(cookieName: string): string | null {
        const cookies = document.cookie.split('; ');
        for (const cookie of cookies) {
            const [key, value] = cookie.split('=');
            if (key === cookieName) {
                return decodeURIComponent(value);
            }
        }
        return null;
    }

    // Opens HeaderV2's #class-config-popup (its show.bs.modal handler loads the
    // data). An explicit handler on the whole pill, instead of relying on
    // Bootstrap's data-toggle delegation, so the arrow/ring/text all open it.
    // The modal's markup is nested inside HeaderV2's .headerbar, which is
    // display:none (styles.css .hv2-legacy-hidden .headerbar) — a modal inside
    // a hidden ancestor can never be shown, so it is re-parented to <body>
    // first (standard Bootstrap practice). Angular keeps its bindings, since
    // it tracks the node itself, not its position in the DOM.
    openClassConfigPopup(): void {
        const modal = document.getElementById('class-config-popup');
        if (!modal) {
            return;
        }
        if (modal.parentElement !== document.body) {
            document.body.appendChild(modal);
        }
        $(modal).modal('show');
    }

    ngOnDestroy(): void {
        this.schoolInfoSub?.unsubscribe();
    }

    private loadClassConfigPercentage(): void {
        this.headerService.getClassConfigStatus('YES', this.resolveAuthToken()).subscribe({
            next: (data: any) => {
                const achieved = data?.achievedCount;
                const total = data?.totalCount;
                const percent = Math.round((achieved * 100) / total);
                this.classConfigPercentage = isNaN(percent) ? 0 : percent;
            },
            error: () => {
                this.classConfigPercentage = 0;
            },
        });
    }

    // Must match the SVG circle's actual radius (r="18" in the template) —
    // this previously used 16, a leftover from an earlier 40x40/r=16
    // viewBox before the ring was resized to 44x44/r=18, so the computed
    // dasharray was shorter than the real circle's circumference and the
    // stroke visibly ran out before completing the loop, even at 100%.
    get ringCircumference(): number {
        return 2 * Math.PI * 18;
    }

    get ringOffset(): number {
        return this.ringCircumference - (this.classConfigPercentage / 100) * this.ringCircumference;
    }

    get greetingPeriod(): 'goodMorning' | 'goodAfternoon' | 'goodEvening' {
        const hour = this.currentDate.getHours();
        if (hour < 12) {
            return 'goodMorning';
        }
        if (hour < 17) {
            return 'goodAfternoon';
        }
        return 'goodEvening';
    }

    get profileInitials(): string {
        const source = this.loginMode === 'WEB_ADMIN' ? this.schoolName : this.staffName;
        return (source || '').slice(0, 2).toUpperCase();
    }

    toggleMobileMenu(): void {
        document.body.classList.toggle('menubar-visible');
    }

    // Mirrors HeaderV2Component.logoutUser() exactly (same API call, same
    // localStorage-clear-except-lang, same cookie cleanup, same toasts) —
    // this is a security-relevant flow (session invalidation), so it's
    // replicated rather than approximated.
    async logout(): Promise<void> {
        try {
            const response = await firstValueFrom(this.headerService.logoutUser());
            const data = response as any;
            if (data?.status) {
                for (const key in localStorage) {
                    if (key !== 'lang') {
                        localStorage.removeItem(key);
                    }
                }
                this.deleteCookiesBasedOnURL();
                this.router.navigate(['/login']);
                const logoutSuccessfullyTranslation = await firstValueFrom(this.translate.get('header.logoutSuccessfully'));
                customToastr(logoutSuccessfullyTranslation, 'success');
            } else {
                const failedToLogoutTranslation = await firstValueFrom(this.translate.get('header.failedToLogoutPleaseTryAgain'));
                customToastr(failedToLogoutTranslation, 'error');
            }
        } catch {
            const oppsSomethingWentWrongTranslation = await firstValueFrom(this.translate.get('header.oppsSomethingWentWrong'));
            customToastr(oppsSomethingWentWrongTranslation);
        }
    }

    private deleteCookiesBasedOnURL(): void {
        if (window.location.hostname.split('.')[1] === 'localhost') {
            this.commonFunction.deleteAllLocalCookies();
        } else {
            this.commonFunction.deleteAllCookies();
        }
    }
}
